import type {
  BuildingService,
  ContextSetpoint,
  DataPointSample,
  DataPointSource,
} from "@/lib/api/building";
import type {
  ZoneEquipmentSignal,
  ZoneKpi,
  ZoneKpis,
} from "@/lib/api/agent";
import type { DataPointDomain, DataPointNature } from "@/lib/building-domain";
import { activeExternalSignalNames } from "@/lib/building-services";
import {
  placeContextSetpointRows,
  type ContextSetpointRowPlacement,
} from "@/lib/context-setpoint-signal";
import { unitsBySourceId } from "@/lib/data-point-labels";
import { formatSignalValue } from "@/lib/format-signal-value";
import { zoneKpiForNativeId } from "@/lib/zone-kpi";
import { is_building_external_row } from "./signal-group-key";
import {
  buildZoneSyoteListRows,
  compareCoveredNativeIds,
} from "./zone-syote-rows";

export type SignaalitMode = "live" | "config";

/** Flat Signaalit inventory row — shared by live and config projections. */
export interface SignaalitRow {
  source_id: string;
  source_name: string;
  source_type: "zone" | "equipment" | "building";
  domain: DataPointDomain;
  nature: DataPointNature;
  name: string;
  unit: string;
  value: string;
  recorded_at: string | undefined;
  component_id: string | null;
  kind: string | null;
  order: number | null;
  connection_source: string | null;
  connection_upstream_name: string | null;
  connection_source_name: string | null;
  connection_source_id: string | null;
  context_setpoint?: ContextSetpointRowPlacement | null;
  zone_kpi?: ZoneKpi | null;
}

export interface ProjectSignaalitRowsInput {
  sources: DataPointSource[];
  samples: DataPointSample[];
  mode: SignaalitMode;
  building_services?: BuildingService[];
  context_setpoints_by_source?: Record<string, ContextSetpoint[]>;
  zone_equipment_signals?: Record<string, ZoneEquipmentSignal[]>;
  zone_kpis?: Record<string, ZoneKpis>;
}

function format_context_setpoint_value(
  value: number | null,
  unit: string,
): string {
  if (value == null) return unit ? `— ${unit}` : "—";
  return `${value.toFixed(1)}${unit ? ` ${unit}` : ""}`;
}

/**
 * Pure Signaalit row projection: membership + row fields for live and config.
 * Search, nature filter, sort, and grouping stay in the renderer.
 */
export function projectSignaalitRows(
  input: ProjectSignaalitRowsInput,
): SignaalitRow[] {
  const {
    sources,
    samples,
    building_services = [],
    context_setpoints_by_source,
    zone_equipment_signals = {},
    zone_kpis = {},
  } = input;
  // mode is part of the shared interface (config vs live); row membership today is driven by
  // the same inputs (empty samples / coverage stay caller-side until the list wires chrome).
  void input.mode;

  const active_external_signal_names =
    activeExternalSignalNames(building_services);

  const upstream_name_by_type = new Map<string, string>();
  for (const src of sources) {
    if (src.equipment_type)
      upstream_name_by_type.set(src.equipment_type, src.source_name);
  }

  const covered_native_by_zone = new Map<string, Set<string>>();
  for (const src of sources) {
    if (src.source_type !== "zone") continue;
    covered_native_by_zone.set(
      src.source_id,
      compareCoveredNativeIds(zone_equipment_signals[src.source_id] ?? []),
    );
  }

  const rows: SignaalitRow[] = [];
  for (const src of sources) {
    const covered = covered_native_by_zone.get(src.source_id);
    for (const nm of src.available_names) {
      if (covered?.has(nm.name)) continue;
      if (
        is_building_external_row(src.source_type, nm.domain) &&
        !active_external_signal_names.has(nm.name)
      ) {
        continue;
      }
      const latest = samples
        .filter((s) => s.source_id === src.source_id && s.name === nm.name)
        .sort((a, b) => b.recorded_at.localeCompare(a.recorded_at))[0];
      rows.push({
        source_id: src.source_id,
        source_name: src.source_name,
        source_type: src.source_type,
        domain: nm.domain,
        nature: nm.nature,
        name: nm.name,
        unit: nm.unit ?? "",
        value: latest
          ? formatSignalValue(
              {
                numeric_value: latest.numeric_value,
                text_value: latest.text_value,
                nature: nm.nature,
              },
              nm.unit,
            )
          : "—",
        recorded_at: latest?.recorded_at,
        component_id: nm.component_id,
        kind: nm.kind,
        order: nm.order,
        connection_source: nm.connection_source,
        connection_upstream_name: nm.connection_upstream_name,
        connection_source_name: nm.connection_source
          ? (upstream_name_by_type.get(nm.connection_source) ?? null)
          : null,
        connection_source_id: null,
        zone_kpi:
          src.source_type === "zone"
            ? zoneKpiForNativeId(zone_kpis[src.source_id], nm.name)
            : null,
      });
    }

    const context_setpoints = context_setpoints_by_source?.[src.source_id];
    if (context_setpoints && context_setpoints.length > 0) {
      const existing_row_names = new Set(
        rows.filter((r) => r.source_id === src.source_id).map((r) => r.name),
      );
      for (const placement of placeContextSetpointRows(
        context_setpoints,
        existing_row_names,
      )) {
        if (placement.merges_with_existing_row) {
          const existing = rows.find(
            (r) =>
              r.source_id === src.source_id &&
              r.name === placement.context_setpoint_id,
          );
          if (existing) existing.context_setpoint = placement;
          continue;
        }
        rows.push({
          source_id: src.source_id,
          source_name: src.source_name,
          source_type: src.source_type,
          domain: "talotekniikka",
          nature: "setpoint",
          name: placement.context_setpoint_id,
          unit: placement.unit,
          value: format_context_setpoint_value(
            placement.effective_value,
            placement.unit,
          ),
          recorded_at: undefined,
          component_id: null,
          kind: null,
          order: null,
          connection_source: null,
          connection_upstream_name: null,
          connection_source_name: null,
          connection_source_id: null,
          context_setpoint: placement,
        });
      }
    }
  }

  const equipment_sources = sources.filter((s) => s.source_type === "equipment");
  const equipment_name_by_id = new Map(
    equipment_sources.map((s) => [s.source_id, s.source_name]),
  );
  const equipment_type_by_id = new Map(
    equipment_sources
      .filter((s) => s.equipment_type)
      .map((s) => [s.source_id, s.equipment_type as string]),
  );
  for (const feed of buildZoneSyoteListRows(
    sources
      .filter((s) => s.source_type === "zone")
      .map((s) => ({ source_id: s.source_id, source_name: s.source_name })),
    zone_equipment_signals,
    equipment_name_by_id,
    equipment_type_by_id,
    unitsBySourceId(equipment_sources),
  )) {
    rows.push({
      source_id: feed.zone_id,
      source_name: feed.zone_name,
      source_type: "zone",
      domain: "talotekniikka",
      nature: "sensor",
      name: feed.name,
      unit: feed.unit,
      value: feed.value,
      recorded_at: feed.recorded_at,
      component_id: null,
      kind: "sensor",
      order: null,
      connection_source: feed.connection_source,
      connection_upstream_name: feed.connection_upstream_name,
      connection_source_name: feed.connection_source_name,
      connection_source_id: feed.connection_source_id,
      zone_kpi: feed.native_id
        ? zoneKpiForNativeId(zone_kpis[feed.zone_id], feed.native_id)
        : null,
    });
  }
  return rows;
}

/**
 * Config-mode “aktivoimatta” suggestions: observed rows that already have a
 * taught Lähdesignaali but are not yet in `declared_data_points`. Observed
 * without lähdesignaali is a kytkennät gap, not an aktivointi suggestion.
 * Excludes asetusarvo and syöte rows.
 */
export function countAktivoimattaSuggestions(
  rows: readonly SignaalitRow[],
  declared: ReadonlySet<string> | readonly string[],
  sourceSignalByCanonical: Readonly<Record<string, string>> = {},
): number {
  const declaredSet =
    declared instanceof Set ? declared : new Set(declared);
  let count = 0;
  for (const row of rows) {
    if (row.context_setpoint) continue;
    if (row.connection_source) continue;
    if (declaredSet.has(row.name)) continue;
    if (!sourceSignalByCanonical[row.name]?.trim()) continue;
    count += 1;
  }
  return count;
}

/**
 * Config-mode “ei kytketty” header gap: unmapped Lähdesignaali names waiting
 * for a Signaali pair. Observed Signaalit that simply lack a taught source are
 * NOT counted — that was a false suggestion (sim/history without a map).
 */
export function countEiKytkettySuggestions(
  unmappedLahdesignaalit: readonly string[] = [],
): number {
  let count = 0;
  for (const name of unmappedLahdesignaalit) {
    if (name.trim()) count += 1;
  }
  return count;
}
